import React from 'react';
import { User, Users, School, Monitor, CheckCircle } from 'lucide-react';
import { classOptions, educationLevels, images } from '../data/mock';

const iconMap = {
  User, Users, School, Monitor
};

const ClassOptions = () => {
  return (
    <section id="kelas" className="py-24 bg-gradient-to-br from-blue-50 to-orange-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(59, 130, 246, 0.15) 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
            <School className="w-4 h-4" />
            Pilihan Kelas
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Pilih <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">Kelas</span> Sesuai Kebutuhan
          </h2>
          <p className="text-lg text-gray-600">
            Kami menyediakan berbagai pilihan kelas yang fleksibel sesuai dengan kebutuhan dan kenyamanan Anda
          </p>
        </div>

        {/* Class Options Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {classOptions.map((option, idx) => {
            const IconComponent = iconMap[option.icon];
            const isPopular = idx === 0;
            return (
              <div
                key={option.id}
                className={`relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group ${
                  isPopular ? 'ring-2 ring-orange-400' : ''
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold rounded-full">
                    Paling Diminati
                  </div>
                )}

                {/* Icon */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/30">
                  {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">{option.name}</h3>
                <p className="text-gray-500 mb-6">{option.description}</p>

                {/* Features */}
                <ul className="space-y-3">
                  {['Jadwal fleksibel', 'Materi lengkap', 'Progress tracking'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Button */}
                <button className="w-full mt-6 py-3 bg-gray-50 text-gray-700 rounded-xl font-semibold hover:bg-blue-600 hover:text-white transition-all duration-300">
                  Pilih Kelas
                </button>
              </div>
            );
          })}
        </div>

        {/* Education Levels */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Jenjang <span className="text-blue-600">Pendidikan</span>
              </h3>
              <p className="text-gray-600 mb-8">
                Genius Education Centre melayani berbagai jenjang pendidikan, dari usia dini hingga dewasa. Setiap jenjang memiliki kurikulum yang disesuaikan dengan kebutuhan perkembangan siswa.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {educationLevels.map((level, idx) => (
                  <div
                    key={level.id}
                    className="group p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 cursor-pointer"
                  >
                    <h4 className="font-bold text-blue-700 group-hover:text-white transition-colors">{level.name}</h4>
                    <p className="text-sm text-blue-500 group-hover:text-blue-100 transition-colors">{level.age}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img 
                    src={images.students1}
                    alt="Siswa Genius" 
                    className="w-full h-40 object-cover rounded-2xl shadow-lg hover:scale-105 transition-transform duration-300"
                  />
                  <img 
                    src={images.classroom2 || images.classroom}
                    alt="Kelas Genius" 
                    className="w-full h-56 object-cover rounded-2xl shadow-lg hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="space-y-4 pt-8">
                  <img 
                    src={images.collaboration}
                    alt="Belajar Bersama" 
                    className="w-full h-56 object-cover rounded-2xl shadow-lg hover:scale-105 transition-transform duration-300"
                  />
                  <img 
                    src={images.modernLearning}
                    alt="Pembelajaran Modern" 
                    className="w-full h-40 object-cover rounded-2xl shadow-lg hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClassOptions;
